import React, { useEffect, useState } from "react";
import { Box, Button } from "@mui/material";
import supabase from "../../supabase";
import { currencyValues, ivaValues } from "../../constants/paymentValues";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { fromStringToNumber } from "../../components/numberInput";
import { RotatingTriangles } from "react-loader-spinner";
import { formatCurrency, formatCurrencyManual } from "../../utils/formatters";
import moment from "moment";
import DataTable from "../../components/ui/DataTable";
import DateRangeFilter from "../../components/ui/DateRangeFilter";
import { Field, FormCard, SelectInput } from "../../components/ui/form";
import { textLabels } from "../../styles/MuiOverrideStyles";
import { tokens } from "../../theme";
import { Num } from "../../components/ui";
import { useIsMobile } from "../../hooks/useIsMobile";
import { colors } from "../../constants/colors";
import { useNavigate } from "react-router-dom";
import { ErrorModal } from "../../components/ModalAlert";
import { LoaderSpinner } from "../../components/spinner/loader";

const DetailCashMovements = ({
  onCashIdSelectedChange,
  onAmountToWithdrawChange,
  onDescriptionChange,
  onCashTypeIdChange,
  allInputsValid,
  setSelectedChecksNumbers,
  searchText,
  setSearchText,
  fechaDesde,
  setFechaDesde,
  isLoading
}) => {
  const [cashId, setCashId] = useState('');
  const [cashIdSelected, setCashIdSelected] = useState('');
  const [cashTypeIdSelected, setCashTypeIdSelected] = useState('');
  const [description, setDescription] = useState('');
  const [amountToWithdraw, setAmountToWithdraw] = useState('');
  const [cashes, setCashes] = useState([]);
  const [cashBalance, setCashBalance] = useState('');
  const [CheckDetails, setCheckDetails] = useState([]);
  const [selectedCheckDetails, setSelectedCheckDetails] = useState([]);
  const [paymentDetail, setPaymentDetail] = useState([]);
  const [editDisable, setEditDisable] = useState(false);
  const [fechaHasta, setFechaHasta] = useState('');
  const [movements, setMovements] = useState([]);
  const [filteredMovements, setFilteredMovements] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [dataLoading, setDataLoading] = useState(false);
  const navigate = useNavigate();
  const isMobile = useIsMobile(768);

  // Función para determinar si debe mostrar una línea separadora gruesa
  const shouldShowThickSeparator = (currentIndex) => {
    // Para el primer registro, comparar con el siguiente
    if (currentIndex === 0) {
      const currentPaymentTicketId = filteredMovements[0]?.paymentTicketId;
      const nextPaymentTicketId = filteredMovements[1]?.paymentTicketId;

      if (
        currentPaymentTicketId &&
        nextPaymentTicketId &&
        currentPaymentTicketId === nextPaymentTicketId
      ) {
        return false; // Línea fina si el primer y segundo registro son del mismo comprobante
      }
      return true; // Línea gruesa en cualquier otro caso
    }

    // Para el resto, comparar con el siguiente como antes
    const currentPaymentTicketId = filteredMovements[currentIndex]?.paymentTicketId;
    const previousPaymentTicketId = filteredMovements[currentIndex + 1]?.paymentTicketId;

    if (
      currentPaymentTicketId &&
      previousPaymentTicketId &&
      currentPaymentTicketId === previousPaymentTicketId
    ) {
      return false;
    }
    return true;
  };

  // Función para obtener el primer día del mes actual en formato YYYY-MM-DD
  const getFirstDayOfCurrentMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  };

  useEffect(() => {
    // Solo establecer la fecha desde como el primer día del mes actual si no hay un valor ya establecido
    if (!fechaDesde) {
      const firstDayOfMonth = getFirstDayOfCurrentMonth();
      setFechaDesde(firstDayOfMonth);
    }
  }, [fechaDesde, setFechaDesde]);

  const handleAmountToWithdrawChange = (e) => {
    const value = e.target.value;
    const regex = /^\d+(\.\d{1,2})?$/;
    if (regex.test(value)) {
      setAmountToWithdraw(value);
      onAmountToWithdrawChange(value);
    
      e.preventDefault();
    }
  };

  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setDescription(value);
    onDescriptionChange(value);
  };

  const handleCashTypeIdChange = (e) => {
    const value = e.target.value;
    setCashTypeIdSelected(value);
    onCashTypeIdChange(value);
  };

  const fetchData = async () => {
    setDataLoading(true);
    try {
      const { data, error } = await supabase.from('cash').select('*').order('cashId', {ascending: true});
      if (error) {
        console.error("Error al obtener los datos: ", error);
      } else {
        setCashes(data);
      }
    } catch (error) {
      console.error("Error en fetchData:", error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Ejecutar búsqueda automáticamente cuando se carga el componente
  useEffect(() => {
    if (fechaDesde) {
      handleSearch();
    }
  }, [fechaDesde]);

  // Calcular total cuando cambien los movimientos filtrados (usar displayAmount: monto * cotización en USD)
  useEffect(() => {
    const total = filteredMovements.reduce((sum, movement) => sum + (movement.displayAmount ?? movement.amount), 0);
    setTotalAmount(total);
  }, [filteredMovements]);

  // Aplicar filtrado inicial cuando se establezca searchText desde la navegación
  useEffect(() => {
    if (searchText && movements.length > 0) {
      const searchLower = searchText.toLowerCase();
      const filteredData = movements.filter(movement => {
        return Object.values(movement).some(value => 
          String(value).toLowerCase().includes(searchLower)
        );
      });
      setFilteredMovements(filteredData);
    }
  }, [searchText, movements]);

  const handleCashIdChange = async (e) => {
    const cashId = e.target.value;
    setCashIdSelected(cashId);
    onCashIdSelectedChange(cashId);
    
    if (cashId === '7') {
      const { data, error } = await supabase
        .from('paymentDetails')
        .select("paymentDetailsIdid, checkIssuerName, checkNumber, checkBank, checkAmount, checkEmitionDate, checkPaymentDate")
        .eq('paymentMethod', 7)
        .eq('checkState', 0);
      if (error) {
        console.error("Error al obtener los detalles de pago: ", error);
      } else {
        setCheckDetails(data);
      }
    }
  };

  /*
    Tildar un cheque sólo lo selecciona: no toca la base. Antes marcaba el
    cheque como cobrado en el acto, así que un clic sin guardar ya movía
    datos y no había forma de volver atrás. El estado se escribe recién al
    guardar la operación, igual que en Retirar dinero.
  */
  const handlePaymentDetailChange = (paymentDetailId, amount) => {
    const isChecked = selectedCheckDetails.includes(paymentDetailId);

    let updatedSelectedCheckDetails;
    if (!isChecked) {
        updatedSelectedCheckDetails = [...selectedCheckDetails, paymentDetailId];
        setAmountToWithdraw(prevAmount => prevAmount + amount);
        onAmountToWithdrawChange(amountToWithdraw + amount);
    } else {
        updatedSelectedCheckDetails = selectedCheckDetails.filter(id => id !== paymentDetailId);
        setAmountToWithdraw(prevAmount => prevAmount - amount);
        onAmountToWithdrawChange(amountToWithdraw - amount);
    }

    setSelectedCheckDetails(updatedSelectedCheckDetails);

    const selectedNumbers = updatedSelectedCheckDetails.map(id => {
        const check = CheckDetails.find(check => check.paymentDetailsIdid === id);
        return check ? check.checkNumber : null;
    }).filter(Boolean).join(' - ');

    setSelectedChecksNumbers(selectedNumbers);
  };

  const handleSearch = async () => {
    setDataLoading(true);
    try {
      // Primero obtenemos todos los movimientos de caja
      let query = supabase
        .from('cashMovements')
        .select(`
          createdAt, 
          description, 
          amount, 
          paymentMethod,
          cashId,
          paymentTicketId,
          cash (description)
        `)
        .order('createdAt', { ascending: false });

      if (cashIdSelected) {
        query = query.eq('cashId', cashIdSelected);
      }

      // Validar y procesar fecha desde
      if (fechaDesde && fechaDesde.trim() !== '') {
        const startDate = new Date(fechaDesde);
        if (startDate instanceof Date && !isNaN(startDate)) {
          // Establecemos la hora al inicio del día en UTC-3
          startDate.setUTCHours(0, 0, 0, 0);
          // Convertimos a UTC sumando 3 horas
          const startDateUTC = new Date(startDate.getTime() + (3 * 60 * 60 * 1000));
          query = query.gte('createdAt', startDateUTC.toISOString());
        }
      }

      // Validar y procesar fecha hasta
      if (fechaHasta && fechaHasta.trim() !== '') {
        const endDate = new Date(fechaHasta);
        if (endDate instanceof Date && !isNaN(endDate)) {
          // Establecemos la hora al final del día en UTC-3
          endDate.setUTCHours(23, 59, 59, 999);
          // Convertimos a UTC sumando 3 horas
          const endDateUTC = new Date(endDate.getTime() + (3 * 60 * 60 * 1000));
          query = query.lte('createdAt', endDateUTC.toISOString());
        }
      }

      if (cashTypeIdSelected === '1') {
        query = query.gt('paymentMethod', 0);
      } else if (cashTypeIdSelected === '2') {
        query = query.lt('paymentMethod', 0);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Error al obtener los movimientos:", error);
      } else {
        const paymentTicketCache = new Map();
        const processedData = await Promise.all(data.map(async (movement) => {
          const movementDate = new Date(movement.createdAt);
          let allTickets = [];
          let paymentId = null;
          let nombreCliente = 'Sin cliente';
          let cotizacion = 1;
          let hasUSD = false;
          
          if (movement.paymentTicketId && paymentTicketCache.has(movement.paymentTicketId)) {
            const cached = paymentTicketCache.get(movement.paymentTicketId);
            allTickets = cached.allTickets;
            paymentId = cached.paymentId;
            nombreCliente = cached.nombreCliente;
            cotizacion = cached.cotizacion;
            hasUSD = cached.hasUSD;
          } else if (movement.paymentTicketId) {
            const { data: paymentTicketData, error: paymentTicketError } = await supabase
              .from('paymentsTicket')
              .select('paymentId')
              .eq('paymentTicketId', movement.paymentTicketId)
              .single();
            
            if (!paymentTicketError && paymentTicketData) {
              paymentId = paymentTicketData.paymentId;
              const { data: allPaymentsTicketData, error: allPaymentsTicketError } = await supabase
                .from('paymentsTicket')
                .select(`
                  paymentTicketId,
                  paymentId,
                  tickets (
                    ticketId,
                    ticketType,
                    ticketNumber,
                    createdAt,
                    clients (
                      clientId,
                      company,
                      name
                    )
                  )
                `)
                .eq('paymentId', paymentId);
              
              if (!allPaymentsTicketError && allPaymentsTicketData && allPaymentsTicketData.length > 0) {
                allTickets = allPaymentsTicketData.map(pt => pt.tickets).filter(Boolean);
                if (allTickets.length > 0 && allTickets[0].clients) {
                  const cliente = allTickets[0].clients;
                  nombreCliente = cliente.company || cliente.name || 'Cliente no especificado';
                }
                const ticketIds = allTickets.map(t => t.ticketId).filter(Boolean);
                if (ticketIds.length > 0) {
                  const { data: ordersData, error: ordersErr } = await supabase
                    .from('orders')
                    .select('ticketId, currencyRate, orderDetails(currency)')
                    .in('ticketId', ticketIds);
                  if (!ordersErr && ordersData && ordersData.length > 0) {
                    const first = ordersData[0];
                    const rate = parseFloat(first.currencyRate);
                    cotizacion = !isNaN(rate) && rate > 0 ? rate : 1;
                    const details = first.orderDetails || [];
                    hasUSD = details.some(d => d && (d.currency === 2 || d.currency === '2'));
                  }
                }
                paymentTicketCache.set(movement.paymentTicketId, {
                  allTickets,
                  paymentId,
                  nombreCliente,
                  cotizacion,
                  hasUSD
                });
              }
            }
          }
          const mult = hasUSD ? cotizacion : 1;
          const displayAmount = (parseFloat(movement.amount) || 0) * mult;
          const montoFormatted = formatCurrencyManual(displayAmount);
          
          return {
            ...movement,
            fecha: movementDate.toLocaleDateString('es-AR'),
            cajaNombre: movement.cash?.description || 'Sin caja',
            comprobante: paymentId ? `Recibo Nro ${paymentId}` : 'Sin comprobante',
            cliente: nombreCliente,
            factura: allTickets.length > 0 ? allTickets.map(ticket => `${ticket.ticketType || ''} ${ticket.ticketNumber || ''}`.trim()).join(', ') : '',
            description: movement.description || '',
            tipo: movement.amount > 0 ? 'Ingreso' : 'Egreso',
            monto: montoFormatted,
            displayAmount,
            allTickets,
            nombreCliente,
            paymentId
          };
        }));
        
        setMovements(processedData);
        setFilteredMovements(processedData);
      }
    } catch (error) {
      console.error("Error en handleSearch:", error);
    } finally {
      setDataLoading(false);
    }
  };

  /**
   * En teléfono las ocho columnas obligaban a scrollear de lado. Se colapsan
   * en una tarjeta por movimiento con la misma información.
   */
  const mobileColumns = [
    {
      name: "fecha",
      label: "Movimiento",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const movement = filteredMovements[tableMeta.rowIndex];
          if (!movement) return null;
          const amount = movement.displayAmount ?? movement.amount;
          const isIncome = Number(amount) >= 0;

          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", padding: "4px 0" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Num sx={{ fontSize: "12.5px", fontWeight: 600 }}>{movement.fecha}</Num>
                <Box
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    borderRadius: 999,
                    padding: "2px 8px",
                    color: isIncome ? "var(--sup-state-ok-ink)" : "var(--sup-state-danger-ink)",
                    backgroundColor: isIncome
                      ? "var(--sup-state-ok-bg)"
                      : "var(--sup-state-danger-bg)",
                  }}
                >
                  {movement.tipo}
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Num
                  sx={{
                    fontSize: "15px",
                    fontWeight: 600,
                    color: isIncome ? "var(--sup-state-ok-ink)" : "var(--sup-state-danger-ink)",
                  }}
                >
                  {formatCurrencyManual(amount)}
                </Num>
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {movement.cliente || "Sin cliente"}
                </Box>
                <Box component="span" sx={{ fontSize: "11.5px", color: tokens.ink3 }}>
                  {[movement.cajaNombre, movement.comprobante].filter(Boolean).join(" · ")}
                </Box>
                {movement.description && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: "11.5px",
                      color: tokens.ink3,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {movement.description}
                  </Box>
                )}
              </Box>
            </Box>
          );
        },
      },
    },
  ];

  const desktopColumns = [
    {
      name: "fecha",
      label: "Fecha",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '100px' } })
      }
    },
    {
      name: "cajaNombre",
      label: "Caja",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '120px' } })
      }
    },
    {
      name: "comprobante",
      label: "Comprobante",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '120px' } })
      }
    },
    {
      name: "cliente",
      label: "Cliente",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '150px' } })
      }
    },
    {
      name: "factura",
      label: "Facturas",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { maxWidth: '200px', wordWrap: 'break-word' } }),
        customBodyRender: (value, tableMeta) => {
          const movement = filteredMovements[tableMeta.rowIndex];
          if (movement.allTickets && movement.allTickets.length > 0) {
            return (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '2px',
                maxWidth: '180px',
                fontSize: '0.85em'
              }}>
                {movement.allTickets.map((ticket, index) => (
                  <React.Fragment key={index}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        const ticketId = parseInt(ticket.ticketId);
                        
                        if (isNaN(ticketId)) {
                          console.error('Invalid ticketId:', ticket.ticketId);
                          ErrorModal({
                            title: "Error",
                            text: "La factura no tiene un ID válido",
                          });
                          return;
                        }
                        
                        const ticketData = {
                          ticketId: ticketId,
                          ticketNumber: ticket.ticketNumber,
                          ticketType: ticket.ticketType,
                          createdAt: ticket.createdAt
                        };
                        
                        // Crear la URL con los datos del ticket como parámetros
                        const ticketParams = new URLSearchParams({
                          ticketId: ticketData.ticketId,
                          ticketNumber: ticketData.ticketNumber,
                          ticketType: ticketData.ticketType,
                          createdAt: ticketData.createdAt
                        });
                        
                        const ticketUrl = `/ticket-details-form?${ticketParams.toString()}`;
                        
                        // Abrir en nueva pestaña
                        window.open(ticketUrl, '_blank');
                      }}
                      style={{ color: 'var(--sup-brand-dark)', textDecoration: 'none', fontSize: '12px', fontWeight: 500 }}
                    >
                      {`${ticket.ticketType || ''} ${ticket.ticketNumber || ''}`.trim()}
                    </a>
                    {index < movement.allTickets.length - 1 ? ', ' : ''}
                  </React.Fragment>
                ))}
              </div>
            );
          }
          return value;
        }
      }
    },
    {
      name: "description",
      label: "Descripción",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '150px' } })
      }
    },
    {
      name: "tipo",
      label: "Tipo",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { minWidth: '100px' } })
      }
    },
    {
      name: "monto",
      label: "Monto",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { textAlign: 'right', minWidth: '120px' } }),
        customBodyRender: (value, tableMeta) => {
          const movement = filteredMovements[tableMeta.rowIndex];
          if (!movement) return value;
          return (
            <span style={{ color: movement.amount > 0 ? 'green' : 'red', display: 'block', textAlign: 'right', width: '100%' }}>
              {value}
            </span>
          );
        }
      }
    }
  ];

  const columns = isMobile ? mobileColumns : desktopColumns;

  const options = {
    responsive: "standard",
    selectableRows: "none",
    sortOrder: {
      name: "createdAt",
      direction: "desc",
    },
    enableNestedDataAccess: ".",
    textLabels,
    pagination: true,
    rowsPerPage: 100,
    rowsPerPageOptions: [10, 25, 50, 100],
    setRowProps: (row, dataIndex, rowIndex) => {
      const hasThickSeparator = shouldShowThickSeparator(rowIndex);
      
      return {
        style: {
          // Línea marcada entre recibos distintos; fina entre renglones del mismo.
          borderBottom: hasThickSeparator
            ? `2px solid ${tokens.borderStrong}`
            : '1px solid #f5f3f1',
        }
      };
    },
    customSearch: (searchQuery, row) => {
      const searchText = searchQuery.toLowerCase();
      return row.some(cell => {
        if (cell === null || cell === undefined) return false;
        const cellValue = String(cell).toLowerCase();
        return cellValue.includes(searchText);
      });
    },
    customFilterDialogFooter: (currentFilterList, applyNewFilters) => {
      return (
        <div style={{ marginTop: '40px' }}>
          <Button variant="contained" onClick={() => applyNewFilters()}>
            Aplicar Filtros
          </Button>
        </div>
      );
    },
    onFilterChange: (changedColumn, filterList, type, changedColumnIndex) => {
      // Actualizar los datos filtrados cuando cambien los filtros
      const filteredData = movements.filter((movement, index) => {
        return filterList.every((filter, filterIndex) => {
          if (filter.length === 0) return true;
          
          const columnName = columns[filterIndex].name;
          const cellValue = movement[columnName];
          
          if (typeof cellValue === 'string') {
            return filter.some(filterValue => 
              cellValue.toLowerCase().includes(filterValue.toLowerCase())
            );
          }
          return filter.some(filterValue => 
            String(cellValue).toLowerCase().includes(filterValue.toLowerCase())
          );
        });
      });
      
      setFilteredMovements(filteredData);
    },
    onSearchChange: (searchText) => {
      setSearchText(searchText);
      
      // Filtrar por búsqueda
      if (searchText) {
        const searchLower = searchText.toLowerCase();
        const filteredData = movements.filter(movement => {
          return Object.values(movement).some(value => 
            String(value).toLowerCase().includes(searchLower)
          );
        });
        setFilteredMovements(filteredData);
      } else {
        setFilteredMovements(movements);
      }
    },
  };

  const rangeFrom = fechaDesde ? moment(fechaDesde, "YYYY-MM-DD").toDate() : null;
  const rangeTo = fechaHasta ? moment(fechaHasta, "YYYY-MM-DD").toDate() : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%" }}>
      {/* Filtros y resultado en una sola tarjeta: la tabla entra sin scrollear. */}
      <FormCard sx={{ padding: { xs: "14px", md: "14px 16px" }, gap: "12px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto",
            },
            gap: "12px",
            alignItems: "end",
          }}
        >
          <Field label="Período">
            <DateRangeFilter
              from={rangeFrom}
              to={rangeTo}
              label="Cualquier fecha"
              fullWidth
              onChange={(from, to) => {
                setFechaDesde(from ? moment(from).format("YYYY-MM-DD") : "");
                setFechaHasta(to ? moment(to).format("YYYY-MM-DD") : "");
              }}
            />
          </Field>

          <Field label="Caja">
            <SelectInput id="cashIdSelected" onChange={handleCashIdChange} defaultValue="">
              <option value="">Todas las cajas</option>
              {Array.isArray(cashes) &&
                cashes.map((cash) => (
                  <option key={cash.cashId} value={cash.cashId}>
                    {cash.description}
                  </option>
                ))}
            </SelectInput>
          </Field>

          <Field label="Tipo de movimiento">
            <SelectInput id="cashTypeIdSelected" onChange={handleCashTypeIdChange} defaultValue="">
              <option value="">Ingresos y egresos</option>
              <option value="1">Sólo ingresos</option>
              <option value="2">Sólo egresos</option>
            </SelectInput>
          </Field>

          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
            sx={{ minHeight: { xs: 44, md: 40 }, minWidth: 140 }}
          >
            Buscar
          </Button>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
            borderTop: `1px solid ${tokens.borderSoft}`,
            paddingTop: "10px",
          }}
        >
          <Box component="span" sx={{ fontSize: "12.5px", color: tokens.ink3, flexGrow: 1 }}>
            Resultado del período ·{" "}
            <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
              {filteredMovements.length}
            </Box>{" "}
            {filteredMovements.length === 1 ? "movimiento" : "movimientos"}
          </Box>
          <Num
            sx={{
              fontSize: "19px",
              fontWeight: 600,
              color: totalAmount >= 0 ? "var(--sup-state-ok-ink)" : tokens.brandDark,
            }}
          >
            {formatCurrencyManual(totalAmount)}
          </Num>
        </Box>
      </FormCard>

      {dataLoading ? (
        <Box
          sx={{
            width: "100%",
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: tokens.surface,
            border: `1px solid ${tokens.border}`,
            borderRadius: `${tokens.radiusCard}px`,
          }}
        >
          <LoaderSpinner />
        </Box>
      ) : (
        <DataTable
          data={filteredMovements}
          columns={columns}
          search={{
            value: searchText || "",
            onChange: (value) => {
              setSearchText(value);
              if (options.onSearchChange) options.onSearchChange(value);
            },
            placeholder: "Buscar por recibo, cliente o descripción…",
          }}
          resultCount={filteredMovements.length}
          totalCount={movements.length}
          emptyMessage="No hay movimientos en el período elegido"
          options={options}
        />
      )}
    </Box>
  );
};

export default DetailCashMovements;
